import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { timelineEvents, categoryColors } from '../data/timelineData';
import { ANNO_FONDAZIONE, annoCorrente } from '../utils/anniUnasca';
import './TimelineClassicaPage.css';

const TimelineClassicaPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [visibleEvents, setVisibleEvents] = useState<Set<number>>(new Set());
  const [scrollProgress, setScrollProgress] = useState<{ [key: number]: number }>({});
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'forward' | 'backward'>('forward');
  const eventRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll orizzontale (solo desktop)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || window.innerWidth < 1024) return;

    // Mostra tutti gli eventi immediatamente
    const allEventIds = new Set(timelineEvents.map(e => e.id));
    setVisibleEvents(allEventIds);

    // Auto-scroll bidirezionale
    const startAutoScroll = () => {
      autoScrollIntervalRef.current = setInterval(() => {
        if (container && !isAutoScrollPaused) {
          const maxScroll = container.scrollWidth - container.clientWidth;
          const currentScroll = container.scrollLeft;

          if (scrollDirection === 'forward') {
            // Scroll in avanti
            if (currentScroll >= maxScroll - 10) {
              // Raggiunta la fine, inverti direzione
              setScrollDirection('backward');
            } else {
              container.scrollBy({ left: 2, behavior: 'auto' });
            }
          } else {
            // Scroll all'indietro
            if (currentScroll <= 10) {
              // Raggiunto l'inizio, inverti direzione
              setScrollDirection('forward');
            } else {
              container.scrollBy({ left: -2, behavior: 'auto' });
            }
          }
        }
      }, 20); // Velocità dello scroll
    };

    startAutoScroll();

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [isAutoScrollPaused, scrollDirection]);

  // Scroll progress per animazioni mobile
  useEffect(() => {
    const handleScroll = () => {
      const newProgress: { [key: number]: number } = {};

      timelineEvents.forEach((event) => {
        const element = eventRefs.current[event.id];
        if (element) {
          const rect = element.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          const elementCenter = rect.top + rect.height / 2;
          const viewportCenter = windowHeight / 2;
          const distance = Math.abs(elementCenter - viewportCenter);
          const maxDistance = windowHeight / 2;
          const progress = Math.max(0, Math.min(1, 1 - distance / maxDistance));

          newProgress[event.id] = progress;
        }
      });

      setScrollProgress(newProgress);
    };

    // Solo su mobile
    if (window.innerWidth < 1024) {
      // Mostra tutti gli eventi su mobile
      const allEventIds = new Set(timelineEvents.map(e => e.id));
      setVisibleEvents(allEventIds);

      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-black">
      {/* Hero Section - Più Dinamico */}
      <section className="relative py-24 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-unasca-green/10 via-blue-500/5 to-purple-500/10 dark:from-unasca-green/20 dark:via-blue-500/10 dark:to-purple-500/20" />
          {/* Animated circles */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-unasca-green/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container-custom relative">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge con animazione */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-unasca-green/20 to-blue-500/20 dark:from-unasca-green/30 dark:to-blue-500/30 rounded-full mb-8 backdrop-blur-sm border border-unasca-green/20 dark:border-unasca-green/30 animate-fade-in">
              <svg className="w-6 h-6 text-unasca-green dark:text-unasca-green-light animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span className="text-sm font-semibold text-unasca-green dark:text-unasca-green-light tracking-wide">
                IL NOSTRO VIAGGIO
              </span>
            </div>

            {/* Titolo con effetto gradiente animato */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 animate-fade-in py-2 pb-5">
              <span className="block bg-gradient-to-r from-gray-900 via-unasca-green to-blue-600 dark:from-white dark:via-unasca-green-light dark:to-blue-400 bg-clip-text text-transparent pb-1">
                Un viaggio straordinario!
              </span>
              <span className="block mt-6 text-4xl md:text-5xl lg:text-6xl bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 dark:from-blue-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent pt-1">
                {ANNO_FONDAZIONE}–{annoCorrente()}
              </span>
            </h1>

            <p className="text-xl md:text-2xl lg:text-3xl text-gray-600 dark:text-gray-400 mb-12 leading-relaxed max-w-3xl mx-auto font-light animate-slide-up">
              I principali obiettivi raggiunti dell'Associazione a favore della categoria
            </p>

            {/* Stats Row - Più visibile */}
            <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-3xl mx-auto mb-12 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-gray-200/50 dark:border-gray-700/50">
                <div className="text-3xl md:text-4xl font-black text-unasca-green dark:text-unasca-green-light mb-1 md:mb-2">1967</div>
                <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 font-medium">Fondazione</div>
              </div>
              <div className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-gray-200/50 dark:border-gray-700/50">
                <div className="text-3xl md:text-4xl font-black text-blue-600 dark:text-blue-400 mb-1 md:mb-2">2000+</div>
                <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 font-medium">Associati</div>
              </div>
              <div className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-gray-200/50 dark:border-gray-700/50">
                <div className="text-3xl md:text-4xl font-black text-purple-600 dark:text-purple-400 mb-1 md:mb-2">5000+</div>
                <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 font-medium leading-tight">
                  <span className="hidden md:inline">Incontri istituzionali</span>
                  <span className="md:hidden">Incontri</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <Link
                to="/diventa-socio"
                className="group relative px-8 py-4 bg-gradient-to-r from-unasca-green to-blue-600 text-white rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Unisciti a Noi
                  <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
              <Link
                to="/blog"
                className="px-8 py-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-full font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-2 border-gray-200 dark:border-gray-700"
              >
                Scopri di Più
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section - DESKTOP: Scroll Orizzontale */}
      <section className="py-20 relative overflow-hidden">
        {/* DESKTOP: Timeline Orizzontale */}
        <div className="hidden lg:block">
          {/* Istruzioni e Controlli */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              {/* Pulsante Indietro */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsAutoScrollPaused(true); // Metti in pausa l'auto-scroll
                  const container = scrollContainerRef.current;
                  if (container) {
                    container.scrollBy({ left: -400, behavior: 'smooth' });
                  }
                }}
                className="p-3 bg-gray-100 dark:bg-gray-800 hover:bg-unasca-green hover:text-white dark:hover:bg-unasca-green-light rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 cursor-pointer"
                aria-label="Scorri indietro"
                type="button"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Pulsante Play/Pause */}
              <button
                onClick={() => setIsAutoScrollPaused(!isAutoScrollPaused)}
                className="p-4 bg-unasca-green dark:bg-unasca-green-light text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110"
                aria-label={isAutoScrollPaused ? 'Riprendi scroll' : 'Pausa scroll'}
              >
                {isAutoScrollPaused ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </button>

              {/* Pulsante Avanti */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsAutoScrollPaused(true); // Metti in pausa l'auto-scroll
                  const container = scrollContainerRef.current;
                  if (container) {
                    container.scrollBy({ left: 400, behavior: 'smooth' });
                  }
                }}
                className="p-3 bg-gray-100 dark:bg-gray-800 hover:bg-unasca-green hover:text-white dark:hover:bg-unasca-green-light rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 cursor-pointer"
                aria-label="Scorri avanti"
                type="button"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Container con scroll orizzontale */}
          <div
            ref={scrollContainerRef}
            className="relative overflow-x-scroll overflow-y-hidden"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#10b981 #f3f4f6',
            }}
            onMouseEnter={() => setIsAutoScrollPaused(true)}
            onMouseLeave={() => setIsAutoScrollPaused(false)}
          >
            {/* Wrapper per contenuto orizzontale */}
            <div className="relative pb-32" style={{ width: 'max-content' }}>
              {/* Timeline Events - Layout Orizzontale */}
              <div className="flex items-end gap-12 px-8 pb-32">
                {timelineEvents.map((event) => {
                  return (
                    <div
                      key={event.id}
                      className="relative flex-shrink-0"
                      style={{ width: '360px' }}
                    >
                      {/* Card principale */}
                      <div className={`
                        relative
                        bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden
                        border-2 border-transparent hover:border-gray-200 dark:hover:border-gray-700
                        transition-all duration-500 transform hover:scale-105
                        group
                      `}>
                        {/* Immagine (se presente) */}
                        {event.image && (
                          <div className="relative h-40 overflow-hidden">
                            <img
                              src={event.image}
                              alt={event.title}
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                            {/* Overlay gradiente */}
                            <div className={`absolute inset-0 bg-gradient-to-t ${categoryColors[event.category]} opacity-20`} />
                          </div>
                        )}

                        {/* Contenuto */}
                        <div className="p-5">
                          {/* Titolo */}
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {event.title}
                          </h3>

                          {/* Descrizione */}
                          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                            {event.description}
                          </p>

                          {/* Badge categoria */}
                          <div className="mt-4 flex items-center gap-2">
                            <span className="text-xl">{event.icon}</span>
                            <div className={`h-1 flex-1 bg-gradient-to-r ${categoryColors[event.category]} rounded-full`} />
                          </div>
                        </div>
                      </div>

                      {/* Palo verticale colorato che scende dalla card alla strada */}
                      <div className={`
                        absolute left-1/2 transform -translate-x-1/2
                        top-full
                        w-1 bg-gradient-to-b ${categoryColors[event.category]}
                        h-24
                      `} />

                      {/* Cerchio colorato sulla strada */}
                      <div className="absolute left-1/2 transform -translate-x-1/2 z-20" style={{ top: 'calc(100% + 96px)' }}>
                        <div className={`
                          w-20 h-20
                          bg-gradient-to-br ${categoryColors[event.category]}
                          rounded-full shadow-2xl
                          flex items-center justify-center
                          border-4 border-white dark:border-gray-900
                          transition-transform duration-300 hover:scale-125
                        `}>
                          <div className="absolute inset-2 bg-white dark:bg-gray-900 rounded-full flex items-center justify-center">
                            <span className={`text-base font-black bg-gradient-to-br ${categoryColors[event.category]} bg-clip-text text-transparent`}>
                              {event.year}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* STRADA ORIZZONTALE - In basso */}
              <div className="absolute left-0 right-0 bottom-0 h-24">
                {/* Asfalto */}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-700 via-gray-800 to-gray-900 dark:from-gray-800 dark:via-gray-900 dark:to-black shadow-2xl">
                  {/* Texture asfalto */}
                  <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)'
                  }} />
                </div>

                {/* Linea tratteggiata centrale */}
                <div className="absolute top-1/2 transform -translate-y-1/2 w-full h-1 overflow-hidden">
                  <div className="w-full h-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400" style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 30px, rgba(255, 255, 255, 1) 30px, rgba(255, 255, 255, 1) 60px)',
                    backgroundSize: '60px 100%',
                    animation: 'roadLineHorizontal 2s linear infinite'
                  }} />
                </div>

                {/* Bordi strada */}
                <div className="absolute top-0 w-full h-1 bg-white/30" />
                <div className="absolute bottom-0 w-full h-1 bg-white/30" />
              </div>
            </div>
          </div>
        </div>

        {/* MOBILE: Timeline Verticale */}
        <div className="lg:hidden container-custom">
          <div className="space-y-12">
            {timelineEvents.map((event) => {
              const progress = scrollProgress[event.id] || 0;

              return (
                <div
                  key={event.id}
                  ref={(el) => { eventRefs.current[event.id] = el; }}
                  className="timeline-event"
                  style={{
                    transform: `scale(${0.9 + progress * 0.1})`,
                    opacity: 0.5 + progress * 0.5,
                  }}
                >
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden">
                    {/* Immagine */}
                    {event.image && (
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          style={{
                            transform: `scale(${1 + progress * 0.05})`,
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        {/* Badge anno */}
                        <div className="absolute top-4 right-4">
                          <div className={`px-4 py-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-xl shadow-lg border-2 border-gray-100 dark:border-gray-800`}>
                            <span className={`text-2xl font-black bg-gradient-to-r ${categoryColors[event.category]} bg-clip-text text-transparent`}>
                              {event.year}
                            </span>
                          </div>
                        </div>
                        {/* Badge categoria */}
                        <div className="absolute bottom-4 left-4">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-100 dark:border-gray-800`}>
                            <span className="text-xl">{event.icon}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Contenuto */}
                    <div className="p-6">
                      {/* Anno se non c'è immagine */}
                      {!event.image && (
                        <div className="flex items-center justify-between mb-4">
                          <div className={`text-5xl font-black bg-gradient-to-r ${categoryColors[event.category]} bg-clip-text text-transparent`}>
                            {event.year}
                          </div>
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r ${categoryColors[event.category]} bg-opacity-10 rounded-full`}>
                            <span className="text-xl">{event.icon}</span>
                          </div>
                        </div>
                      )}

                      {/* Titolo */}
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                        {event.title}
                      </h3>

                      {/* Descrizione */}
                      <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                        {event.description}
                      </p>

                      {/* Linea decorativa */}
                      <div className="mt-4 h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${categoryColors[event.category]} rounded-full transition-all duration-500`}
                          style={{
                            width: `${progress * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Conclusione Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-black">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="prose prose-lg dark:prose-invert mx-auto">
              <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Dal <strong className="text-unasca-green dark:text-unasca-green-light">1967</strong> a oggi, UNASCA si è evoluta da semplice associazione di categoria a <strong>interlocutore istituzionale riconosciuto</strong> a livello nazionale ed europeo.
              </p>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Ha contribuito all'<strong>innovazione del sistema della formazione alla guida</strong>, alla <strong>digitalizzazione dei servizi automobilistici</strong> e alla promozione di una <strong>mobilità più sicura e sostenibile</strong>.
              </p>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                Il suo ruolo è oggi <strong className="text-blue-600 dark:text-blue-400">strategico</strong> nella transizione verso la nuova mobilità e nel rafforzamento della <strong className="text-purple-600 dark:text-purple-400">cultura della legalità</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-unasca-green to-blue-600 dark:from-unasca-green-dark dark:to-blue-800">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Fai Parte della Storia
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Unisciti a UNASCA e contribuisci a scrivere i prossimi capitoli della nostra storia
            </p>
            <Link
              to="/diventa-socio"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-unasca-green rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
            >
              <span>Diventa Socio</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TimelineClassicaPage;

